import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import apiManager from '../../../lib/api';
import { Category } from '../../../lib/api/models';
import { motion } from 'framer-motion';
import { T, useTranslate } from '@tolgee/react';

const CategoriesPage = () => {
  const { t } = useTranslate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiManager.categories.getCategories();

        if (response.success && response.data) {
          setCategories(response.data);
        } else {
          setError(t('admin.categories.error_loading'));
        }
      } catch (err) {
        console.error('Error al cargar categorías:', err);
        setError(t('admin.categories.error_updating'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [t]);

  // Filtrar categorías según los criterios de búsqueda y estado
  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = selectedStatus === 'all' ||
      (selectedStatus === 'active' && category.isActive) ||
      (selectedStatus === 'inactive' && !category.isActive);

    return matchesSearch && matchesStatus;
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // La búsqueda se realiza automáticamente mediante filteredCategories
  };

  const handleStatusChange = async (categoryId: string, newStatus: boolean) => {
    try {
      const response = await apiManager.categories.updateCategory(categoryId, { isActive: newStatus });

      if (response.success) {
        // Actualizar el estado local
        setCategories(prevCategories =>
          prevCategories.map(cat =>
            cat.id === categoryId ? { ...cat, isActive: newStatus } : cat
          )
        );
      } else {
        setError(t('admin.categories.error_updating'));
      }
    } catch (err) {
      console.error('Error al actualizar estado de categoría:', err);
      setError(t('admin.categories.error_updating'));
    }
  };

  // Obtener la jerarquía de la categoría usando el parent populado
  const getCategoryHierarchy = (category: Category): string => {
    // Si tiene parent como objeto (populado), mostrar "Parent > Nombre"
    if (category.parent && typeof category.parent === 'object' && (category.parent as any).name) {
      return `${(category.parent as any).name} > ${category.name}`;
    }
    // Si no tiene parent o es null, solo mostrar el nombre (es categoría raíz)
    return category.name;
  };

  return (
    <AdminLayout title={t('admin.categories.title', 'Gestión de Categorías')}>
      {/* Filtros y búsqueda */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <form onSubmit={handleSearch} className="col-span-1 md:col-span-3">
            <div className="relative">
              <input
                type="text"
                placeholder={t('admin.categories.search_placeholder')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                type="submit"
                className="absolute right-0 top-0 h-full px-4 text-gray-600 hover:text-indigo-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </form>
          <div>
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as 'all' | 'active' | 'inactive')}
            >
              <option value="all">{t('admin.categories.all_statuses')}</option>
              <option value="active">{t('admin.categories.active')}</option>
              <option value="inactive">{t('admin.categories.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Botón para añadir nueva categoría */}
      <div className="mb-6 flex justify-end">
        <Link
          to="/admin/categories/new"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <T keyName="admin.categories.add_category">Añadir categoría</T>
        </Link>
      </div>

      {/* Lista de categorías */}
      <div className="bg-white shadow rounded-lg">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
          </div>
        ) : error ? (
          <div className="p-4 text-red-700 bg-red-100 rounded-md">
            <p>{error}</p>
          </div>
        ) : filteredCategories.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <T keyName="admin.categories.name">Nombre</T>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <T keyName="admin.categories.slug">Slug</T>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <T keyName="admin.categories.hierarchy">Jerarquía</T>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <T keyName="admin.categories.status">Estado</T>
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <T keyName="admin.categories.actions">Acciones</T>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCategories.map((category, index) => (
                  <motion.tr
                    key={category.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {category.image && (
                          <div className="flex-shrink-0 h-10 w-10 mr-4">
                            <img className="h-10 w-10 rounded-md object-cover" src={category.image} alt={category.name} />
                          </div>
                        )}
                        <div className="text-sm font-medium text-gray-900">{category.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {category.slug}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getCategoryHierarchy(category)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${category.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                        {category.isActive ? t('admin.categories.active') : t('admin.categories.inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleStatusChange(category.id, !category.isActive)}
                          className={`text-xs px-2 py-1 rounded ${category.isActive
                            ? 'bg-red-50 text-red-600 hover:bg-red-100'
                            : 'bg-green-50 text-green-600 hover:bg-green-100'
                            }`}
                        >
                          {category.isActive ? t('admin.categories.deactivate') : t('admin.categories.activate')}
                        </button>
                        <Link
                          to={`/admin/categories/${category.id}/edit`}
                          className="text-xs px-2 py-1 bg-indigo-50 text-indigo-600 rounded hover:bg-indigo-100"
                        >
                          <T keyName="admin.categories.edit">Editar</T>
                        </Link>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p><T keyName="admin.categories.no_categories_found">No se encontraron categorías que coincidan con los criterios de búsqueda.</T></p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CategoriesPage;
